import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Check,
  Sparkles,
  Zap,
  Crown,
  Loader2,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const { user, getAuthHeader, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      fetchCurrentSubscription();
    }
  }, [isAuthenticated]);

  // Check for success redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && isAuthenticated) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, isAuthenticated]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/my`, {
        headers: getAuthHeader()
      });
      setCurrentPlan(response.data.plan);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: getAuthHeader()
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setCurrentPlan(response.data.plan_id);
        // Clear the URL params
        navigate('/pricing', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        navigate('/pricing', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    if (planId === 'free') {
      toast.info('You are already on the Free plan');
      return;
    }

    if (planId === currentPlan) {
      toast.info('You are already subscribed to this plan');
      return;
    }

    setCheckingOut(planId);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free': return Zap;
      case 'unlimited': return Crown;
      default: return Sparkles;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'free': return 'text-muted-foreground';
      case 'unlimited': return 'text-amber-500';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-primary mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan, index) => {
            const Icon = getPlanIcon(plan.id);
            const isCurrentPlan = plan.id === currentPlan;
            const isPopular = plan.id === 'unlimited';

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`relative h-full flex flex-col ${isPopular ? 'border-primary shadow-lg scale-105' : 'border-border'} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}>
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="absolute -top-3 right-4">
                      <Badge variant="secondary">Current Plan</Badge>
                    </div>
                  )}

                  <CardHeader className="text-center pb-2">
                    <div className={`w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4 ${getPlanColor(plan.id)}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="font-serif text-2xl">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">${plan.price}</span>
                      {plan.price > 0 && <span className="text-muted-foreground">/month</span>}
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col">
                    <ul className="space-y-3 flex-1 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full gap-2 ${isPopular ? 'bg-primary hover:bg-primary/90' : ''}`}
                      variant={isCurrentPlan ? 'outline' : (isPopular ? 'default' : 'outline')}
                      disabled={isCurrentPlan || checkingOut === plan.id}
                      onClick={() => handleSubscribe(plan.id)}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {checkingOut === plan.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : isCurrentPlan ? (
                        'Current Plan'
                      ) : plan.price === 0 ? (
                        'Get Started Free'
                      ) : (
                        <>
                          Subscribe
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-muted-foreground text-sm">
            All plans include a 30-day money-back guarantee. No questions asked.
          </p>
          <p className="text-muted-foreground text-sm mt-2">
            Need a custom plan for your school? <a href="mailto:homeclassem@gmail.com" className="text-primary hover:underline">Contact us</a>
          </p>
        </motion.div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Terms
              </Link>
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Privacy
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 HomeClass. All rights reserved.
            </p>
            <p className="text-sm text-muted-foreground">
              For support, contact us at{' '}
              <a 
                href="mailto:homeclassem@gmail.com" 
                className="text-primary hover:underline"
              >
                homeclassem@gmail.com
              </a>
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default PricingPage;
